// --------------------------------------------------------------
// Created On: 2025-04-30
// Author: Hannah Vaughan
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo, useState } from "react";
import styles from "./DetailsPage.module.scss";
import IconPickerMapMarker from "src/components/IconPickerMapMarker/IconPickerMapMarker";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import {
  COPILOT_DEVICE_TYPE,
  COPILOT_EDGE_DEVICE_TYPE,
  COPILOT_PMG_DEVICE_TYPE,
  COPILOT_MHM_DEVICE_TYPE,
  DATA_COLLECTION_MONITORING_DEVICE,
  DATA_COLLECTION_NONE,
  DATA_COLLECTION_OTHER_ASSET,
  MAX_ASSET_FIELDS_LENGTH,
  MAX_ASSET_NAME_LENGTH,
  MAX_DEVICE_IDENTIFIER_LENGTH,
  PULSE_DEVICE_TYPE,
  SEEKER_C_DEVICE_TYPE,
  SEEKER_SOLAR_DEVICE_TYPE,
  SCADA_RTU_DEVICE_TYPE,
  SCADA_LITE_DEVICE_TYPE,
} from "src/constants/miscellaneous";
import Warning from "src/components/Warning/Warning";
import ClaimDeviceModal from "src/components/ClaimDeviceModal/ClaimDeviceModal";

// Page of the Asset Management Wizard that allows the user to enter asset detail information,
// like name, template, monitoring device, gateway asset, and more.
export default function DetailsPage(props: Props): Component {
  const [showClaimDeviceModal, setShowClaimDeviceModal] = useState<boolean>(false);
  const [deviceTypeImageSource, deviceTypeImageAlt] = useMemo(
    () => getDeviceTypeImageDetails(props.deviceType),
    [props.deviceType]
  );

  // Gets the display name of the currently selected device type, falling back to its model if it cannot be found.
  function getDeviceTypeName(): string {
    const selectedDeviceType = props.deviceTypes.find((deviceType) => deviceType.model === props.deviceType);
    if (selectedDeviceType !== undefined) {
      return selectedDeviceType.name;
    } else {
      return props.deviceType;
    }
  }

  // Updates the source and alt values for the device type image whenever the device type is changed.
  function getDeviceTypeImageDetails(deviceType: string): [string, string] {
    let newDeviceTypeImageSource = "";
    let newDeviceTypeImageAlt = "";
    switch (deviceType) {
      case PULSE_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_pulse.jpg";
        newDeviceTypeImageAlt = "Pulse identifier.";
        break;
      case SEEKER_C_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_seeker_c.jpg";
        newDeviceTypeImageAlt = "Seeker C identifier.";
        break;
      case SEEKER_SOLAR_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_seeker_solar.jpg";
        newDeviceTypeImageAlt = "Seeker Solar identifier.";
        break;
      case COPILOT_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_copilot.jpg";
        newDeviceTypeImageAlt = "Co-Pilot identifier.";
        break;
      case COPILOT_PMG_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_pmg.jpg";
        newDeviceTypeImageAlt = "Co-Pilot PMG identifier.";
        break;
      case COPILOT_MHM_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_copilot_mhm.jpg";
        newDeviceTypeImageAlt = "Co-Pilot MHM identifier.";
        break;
      case SCADA_RTU_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_scada_rtu.jpg";
        newDeviceTypeImageAlt = "SCADA RTU.";
        break;
      case SCADA_LITE_DEVICE_TYPE:
        newDeviceTypeImageSource = "/images/serial_number_scada_rtu.jpg";
        newDeviceTypeImageAlt = "SCADA Lite RTU.";
        break;
      default:
        newDeviceTypeImageSource = "";
        newDeviceTypeImageAlt = "";
    }
    return [newDeviceTypeImageSource, newDeviceTypeImageAlt];
  }

  return (
    <Fragment>
      <div className={`${styles.assetNameBody} col mx-3`}>
        {props.isCreatingNewAsset ? (
          <Fragment>
            <label className={`${styles.title} mb-3`}>Asset Name&nbsp;</label>
            <IconTooltip
              id="asset-name-tooltip"
              icon="info-circle"
              message="The name your asset will have displayed. Each asset must have a unique name."
              color="var(--info-tooltip)"
            />
            <input
              data-test="asset-name-input"
              className="form-control mx-auto"
              type="text"
              maxLength={MAX_ASSET_NAME_LENGTH}
              value={props.name}
              disabled={props.disabled}
              onChange={(e) => props.onChangeName(e.target.value)}
            />
          </Fragment>
        ) : (
          <input
            data-test="asset-name-input"
            className={`${styles.name} form-control px-0 mx-auto`}
            type="text"
            maxLength={MAX_ASSET_NAME_LENGTH}
            value={props.name}
            disabled={props.disabled}
            onChange={(e) => props.onChangeName(e.target.value)}
          />
        )}
      </div>
      <div className="col m-3">
        <label className={`${styles.title} mb-3`}>Asset Nickname&nbsp;</label>
        <IconTooltip
          id="asset-nickname-tooltip"
          icon="info-circle"
          message="An optional descriptive name that will sometimes be displayed along side the asset's name."
          color="var(--info-tooltip)"
        />
        <input
          data-test="asset-nickname-input"
          className="form-control mx-auto"
          maxLength={MAX_ASSET_FIELDS_LENGTH}
          disabled={!props.formIsEditable}
          value={props.nickname}
          onChange={(e) => props.onChangeNickname(e.target.value)}
        />
      </div>
      <div className="col m-3">
        <label className={`${styles.title} mb-3`}>Template&nbsp;</label>
        <IconTooltip
          id="asset-template-tooltip"
          icon="info-circle"
          message={
            "The template that determines what is displayed when looking at a single asset's details. " +
            "If it a template is modified, then the asset detail pages for each associated asset is also modified."
          }
          color="var(--info-tooltip)"
        />
        <select
          data-test="asset-template-selection"
          className="form-select"
          value={props.templateId === null ? "" : props.templateId}
          disabled={props.disabled}
          onChange={(e) => props.onChangeTemplateId(parseInt(e.target.value, 10))}
        >
          <option value="" disabled>
            Please select a template...
          </option>
          {props.templates.map((template) => (
            <option key={template.assetTemplateId} value={template.assetTemplateId}>
              {template.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col m-3">
        <label className={`${styles.title} mb-3`}>Map Icon&nbsp;</label>
        <IconTooltip
          id="asset-map-icon-tooltip"
          icon="info-circle"
          message="The icon that is used for displaying the asset on the map."
          color="var(--info-tooltip)"
        />
        <IconPickerMapMarker
          assetMapIcons={props.assetMapIcons}
          selectedAssetMapIconId={props.assetMapIconId}
          disabled={!props.formIsEditable}
          onChange={(mapIconId) => props.onChangeMapIconId(mapIconId)}
        />
      </div>
      <div className="col m-3">
        <label className={`${styles.title} mb-3`}>How does this asset collect data?&nbsp;</label>
        <IconTooltip
          id="asset-data-collection-tooltip"
          icon="info-circle"
          message="The origin of and method in which data is collected for the asset."
          color="var(--info-tooltip)"
        />
        <select
          data-test="asset-data-collection-selection"
          className="form-select"
          value={props.dataCollectionId}
          disabled={props.disabled}
          onChange={(e) => props.onChangeDataCollectionId(parseInt(e.target.value, 10))}
        >
          <option value={-1} disabled>
            Select an option...
          </option>
          <option value={DATA_COLLECTION_NONE}>It does not collect data</option>
          <option value={DATA_COLLECTION_MONITORING_DEVICE}>It collects data using a monitoring device</option>
          <option value={DATA_COLLECTION_OTHER_ASSET}>It collects data from another asset</option>
        </select>
      </div>
      {props.dataCollectionId === DATA_COLLECTION_MONITORING_DEVICE && (
        <Fragment>
          <div className="col m-3">
            <label className={`${styles.title} mb-3`}>Monitoring Device Model&nbsp;</label>
            <IconTooltip
              id="monitoring-device-tooltip"
              icon="info-circle"
              message={
                "Monitoring devices that are attached to the product. Monitoring devices track " +
                "data about the product, such as temperature and vibration."
              }
              color="var(--info-tooltip)"
            />
            <select
              data-test="device-type-selection"
              className="form-select"
              value={props.deviceType}
              disabled={props.disabled}
              onChange={(e) => props.onChangeDeviceType(e.target.value)}
            >
              <option value="" disabled>
                Please select a monitoring device model...
              </option>
              {props.deviceTypes.map((deviceType) => (
                <option key={deviceType.deviceTypeId} value={deviceType.model}>
                  {deviceType.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col m-3">
            <label className={`${styles.title} mb-3`}>Identifier&nbsp;</label>
            <IconTooltip
              id="device-identifier-tooltip"
              icon="info-circle"
              message="The monitoring device identifier that can be seen on the physical device (see image below)."
              color="var(--info-tooltip)"
            />
            <input
              data-test="device-identifier-input"
              className="form-control mx-auto"
              type="text"
              list="datalist-devices"
              autoComplete="off"
              maxLength={MAX_DEVICE_IDENTIFIER_LENGTH}
              disabled={props.disabled}
              value={props.deviceIdentifier}
              onChange={(e) => props.onChangeDeviceIdentifier(e.target.value)}
            />
            <datalist id="datalist-devices">
              {props.monitoringDevices !== undefined &&
                props.monitoringDevices.map((monitoringDevice) => (
                  <option
                    key={`${monitoringDevice.deviceType}-${monitoringDevice.deviceIdentifier}`}
                    value={monitoringDevice.deviceIdentifier}
                  />
                ))}
            </datalist>
            {props.deviceType === COPILOT_EDGE_DEVICE_TYPE && (
              <div className="my-3">
                <button
                  data-test="claim-edge-device-button"
                  type="button"
                  className="btn btn-primary"
                  disabled={props.disabled}
                  onClick={() => setShowClaimDeviceModal(true)}
                >
                  Claim Monitoring Device
                </button>
              </div>
            )}
          </div>
          {props.deviceType !== "" && deviceTypeImageSource !== "" && (
            <div className="col m-3">
              <img className={styles.image} src={deviceTypeImageSource} alt={deviceTypeImageAlt} />
            </div>
          )}
          <div className="col m-3">
            <div className="form-check">
              <input
                data-test="hide-gateway-data-checkbox"
                className="form-check-input"
                type="checkbox"
                disabled={props.disabled}
                checked={props.hideGatewayData}
                onChange={() => props.onChangeHideGatewayData()}
                id="hideGatewayDataCheck"
              />
              <label className="form-check-label" htmlFor="hideGatewayDataCheck">
                <span>Do not store data from this monitoring device (Bridge mode)</span>
                <IconTooltip
                  id="hide-gateway-data-tooltip"
                  icon="info-circle"
                  message={
                    "Monitoring devices in bridge mode do not store data coming from the monitoring device. " +
                    "They will still store and report data from connected sensors and controllers."
                  }
                  color="var(--info-tooltip)"
                />
              </label>
            </div>
          </div>
          <div className="col m-3">
            <div className="form-check">
              <input
                data-test="is-gateway-checkbox"
                className="form-check-input"
                type="checkbox"
                disabled={props.disabled}
                checked={props.isGateway}
                onChange={(e) => props.onChangeIsGateway(e.target.checked)}
                id="isGatewayCheck"
              />
              <label className="form-check-label" htmlFor="isGatewayCheck">
                <span>This asset passes data to other assets (Gateway)</span>
                <IconTooltip
                  id="is-gateway-tooltip"
                  icon="info-circle"
                  message={
                    "Enable this setting when you want to have a single monitoring device report data for multiple " +
                    "asset. This asset will create a network that other assets can be configured to use. Other " +
                    "assets on the network can collect data from a controller or sensors attached to the monitoring " +
                    "device."
                  }
                  color="var(--info-tooltip)"
                />
              </label>
            </div>
          </div>
        </Fragment>
      )}
      <ClaimDeviceModal
        showModal={showClaimDeviceModal}
        deviceType={props.deviceType}
        deviceTypeName={getDeviceTypeName()}
        onDeviceClaimed={(deviceType, deviceIdentifier) => props.onDeviceClaimed(deviceType, deviceIdentifier)}
        onClose={() => setShowClaimDeviceModal(false)}
      />
      {props.dataCollectionId === DATA_COLLECTION_OTHER_ASSET && (
        <div className="col m-3">
          <label className={`${styles.title} mb-3`}>
            <span>Name of gateway asset that is providing data</span>
            <IconTooltip
              id="gateway-asset-name-tooltip"
              icon="info-circle"
              message={
                "This asset will join the network of the named gateway asset. This asset will be able to collect " +
                "data from a controller or sensors attached to the gateway asset's monitoring devices."
              }
              color="var(--info-tooltip)"
            />
          </label>
          <input
            data-test="gateway-asset-name-input"
            className="form-control mx-auto"
            type="text"
            list="datalist-gateway-assets"
            autoComplete="off"
            disabled={
              props.disabled ||
              props.gatewayAssets === null ||
              props.gatewayAssets === undefined ||
              props.gatewayAssets.length === 0
            }
            maxLength={MAX_ASSET_NAME_LENGTH}
            value={props.gatewayAssetName}
            onChange={(e) => props.onSearchGatewayAssets(e.target.value)}
          />
          <datalist id="datalist-gateway-assets">
            {props.gatewayAssets !== null &&
              props.gatewayAssets !== undefined &&
              props.gatewayAssets.map((gatewayAsset) => (
                <option key={gatewayAsset.assetId} value={gatewayAsset.name} />
              ))}
          </datalist>
          {props.gatewayAssets === null ||
            props.gatewayAssets === undefined ||
            (props.gatewayAssets.length === 0 && (
              <div className="mt-3">
                <Warning
                  message={
                    "No gateway assets could be found. Please select another option for data " +
                    "collection or create a gateway asset first."
                  }
                />
              </div>
            ))}
        </div>
      )}
    </Fragment>
  );
}

interface Props {
  isCreatingNewAsset: boolean;
  name: string;
  nickname: string;
  templateId: number | null;
  templates: Template[];
  assetMapIconId: number | null;
  assetMapIcons: AssetMapIcon[];
  dataCollectionId: number;
  deviceTypes: DeviceType[];
  deviceType: string;
  deviceIdentifier: string;
  monitoringDevices: MonitoringDevice[];
  gatewayAssets: Asset[];
  hideGatewayData: boolean;
  isGateway: boolean;
  gatewayAssetName: string;
  formIsEditable: boolean;
  disabled: boolean;
  onChangeName: (name: string) => void;
  onChangeNickname: (nickname: string) => void;
  onChangeTemplateId: (templateId: number | null) => void;
  onChangeMapIconId: (mapIconId: number | null) => void;
  onChangeDataCollectionId: (dataCollectionId: number) => void;
  onChangeDeviceType: (deviceType: string) => void;
  onChangeDeviceIdentifier: (deviceIdentifier: string) => void;
  onDeviceClaimed: (deviceType: string, deviceIdentifier: string) => void;
  onChangeIsGateway: (isGateway: boolean) => void;
  onChangeHideGatewayData: () => void;
  onSearchGatewayAssets: (filter: string) => void;
}

interface Template {
  assetTemplateId: number;
  name: string;
  isImmutable: boolean;
}

interface AssetMapIcon {
  assetMapIconId: number;
  name: string;
  code: string;
}

interface DeviceType {
  deviceTypeId: number;
  model: string;
  name: string;
}

interface MonitoringDevice {
  formId?: number;
  deviceId: number;
  deviceType: string;
  deviceIdentifier: string;
  attributes?: DeviceAttribute[];
  deviceLog?: DeviceLog;
}

interface DeviceAttribute {
  code: string;
  name: string;
}

type DeviceLog = {
  lastUpdated: DeviceLogUnit;
} & {
  [key: string]: DeviceLogUnit;
};

interface DeviceLogUnit {
  code: string;
  name: string;
  value: string | number | boolean;
  latitude?: number;
  longitude?: number;
  unitLong: string;
  unitShort: string;
  unitSymbol: string;
  icon: string;
  isHistorical: boolean;
  sensorConnected?: boolean;
  mostRecentUtc?: string;
}

interface Asset {
  assetId: number;
  name: string;
  nickname: string;
  isRented?: boolean;
  isMigrating?: boolean;
  isGateway: boolean;
  lastConfigSuccessful?: boolean;
}
